import { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import type { z } from 'zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { Loader2, AlertCircle, CheckCircle, X, Trash2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useVinDecoder } from '@/hooks/useVinDecoder';
import {
  MANUAL_STOP_LIMITS,
  getManualStopReasonsFor,
  isRecallReason,
  type StopKind,
  manualStopVehicleSchema
} from '@/lib/validation/manualStopValidation';
import { useCampaigns, Campaign } from '@/hooks/useCampaigns';
import { FieldError } from './FieldError';

// ===========================================================================
// VehicleEntryRow — one VIN / Reason / Notes block, repeated per vehicle
// ===========================================================================
/** One vehicle, shaped for manualStopVehicleSchema (campaigns stay grouped per vehicle). */
export type VehicleEntryData = z.input<typeof manualStopVehicleSchema>;

export interface VehicleEntryHandle {
  /** Checks that need component state (VIN decoded via NHTSA). Schema checks run on submit. */
  validate: () => string | null;
  getData: () => VehicleEntryData;
}

interface VehicleEntryRowProps {
  index: number;
  /** Limits the reason list (customer stops can't be auction work). */
  stopKind: StopKind;
  canRemove: boolean;
  onRemove: () => void;
  /** Schema errors for this vehicle, keyed by field (vin, manual_stop_reason, custom_reason). */
  errors?: Record<string, string>;
}

const toCampaignInput = (c: Campaign) => ({
  campaign_number: c.campaign_number,
  ...(c.recall_title && { recall_title: c.recall_title })
});

export const VehicleEntryRow = forwardRef<VehicleEntryHandle, VehicleEntryRowProps>(
  // eslint-disable-next-line max-lines-per-function, complexity
  ({ index, stopKind, canRemove, onRemove, errors = {} }, ref) => {
    const vinDecoder = useVinDecoder();
    const { data: campaignsData } = useCampaigns();
    const campaigns = campaignsData?.campaigns || [];

    const [reason, setReason] = useState('');
    const [customReason, setCustomReason] = useState('');
    const [selectedCampaigns, setSelectedCampaigns] = useState<Campaign[]>([]);
    const [campaignSearch, setCampaignSearch] = useState('');

    const reasonOptions = getManualStopReasonsFor(stopKind);
    const showCampaignField = isRecallReason(reason);

    // Switching Business → Customer drops a reason that no longer applies (and its campaigns).
    useEffect(() => {
      if (reason && !reasonOptions.some(r => r.value === reason)) {
        setReason('');
        setSelectedCampaigns([]);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps -- only react to a stop type change
    }, [stopKind]);

    const filteredCampaigns = campaigns.filter(
      (c: Campaign) =>
        c.campaign_number.toLowerCase().includes(campaignSearch.toLowerCase()) ||
        (c.recall_title && c.recall_title.toLowerCase().includes(campaignSearch.toLowerCase()))
    );

    const handleSelectCampaign = (campaign: Campaign) => {
      if (selectedCampaigns.some(c => c.campaign_number === campaign.campaign_number)) {
        setCampaignSearch('');
        return;
      }
      setSelectedCampaigns(prev => [...prev, campaign]);
      setCampaignSearch('');
    };

    const handleRemoveCampaign = (campaignNumber: string) => {
      setSelectedCampaigns(prev => prev.filter(c => c.campaign_number !== campaignNumber));
    };

    useImperativeHandle(
      ref,
      () => ({
        validate: () => {
          if (!vinDecoder.vehicleInfo) return 'Please enter a valid VIN';
          if (!reason) return 'Please select a reason for the stop';
          if (reason === 'other' && !customReason.trim()) return 'Please enter a custom reason';
          return null;
        },
        getData: () => ({
          vin: vinDecoder.vin,
          manual_stop_reason: reason as VehicleEntryData['manual_stop_reason'],
          ...(reason === 'other' && { custom_reason: customReason }),
          ...(vinDecoder.vehicleInfo?.year && { vehicle_year: vinDecoder.vehicleInfo.year }),
          ...(vinDecoder.vehicleInfo?.make && { vehicle_make: vinDecoder.vehicleInfo.make }),
          ...(vinDecoder.vehicleInfo?.model && { vehicle_model: vinDecoder.vehicleInfo.model }),
          campaigns: showCampaignField ? selectedCampaigns.map(toCampaignInput) : []
        })
      }),
      [
        vinDecoder.vin,
        vinDecoder.vehicleInfo,
        reason,
        customReason,
        selectedCampaigns,
        showCampaignField
      ]
    );

    const vinInputId = `manual-stop-vin-${index}`;
    const reasonId = `manual-stop-reason-${index}`;

    return (
      <div className='border rounded-lg p-3 space-y-3 bg-card'>
        <div className='flex items-center justify-between'>
          <span className='text-xs font-semibold uppercase tracking-wide text-muted-foreground'>
            Vehicle {index + 1}
          </span>
          {canRemove && (
            <Button
              type='button'
              variant='ghost'
              size='sm'
              onClick={onRemove}
              className='h-7 gap-1 text-destructive hover:bg-destructive/10 hover:text-destructive'
            >
              <Trash2 className='h-3.5 w-3.5' />
              Remove
            </Button>
          )}
        </div>

        <div className='space-y-2'>
          <Label htmlFor={vinInputId}>
            VIN <span className='text-destructive'>*</span>
          </Label>
          <Input
            id={vinInputId}
            placeholder='Enter 17-character VIN'
            value={vinDecoder.vin}
            onChange={e => vinDecoder.handleChange(e.target.value)}
            onBlur={vinDecoder.handleBlur}
            maxLength={17}
            className={cn(
              'font-mono uppercase',
              (vinDecoder.error || errors['vin']) && 'border-destructive',
              vinDecoder.vehicleInfo && !errors['vin'] && 'border-green-500'
            )}
          />
          {!vinDecoder.error && <FieldError message={errors['vin']} />}
          {vinDecoder.isDecoding && (
            <p className='text-xs text-muted-foreground flex items-center gap-1'>
              <Loader2 className='h-3 w-3 animate-spin' /> Decoding VIN…
            </p>
          )}
          {vinDecoder.error && (
            <p className='text-xs text-destructive flex items-start gap-1'>
              <AlertCircle className='h-3 w-3 mt-0.5 shrink-0' /> {vinDecoder.error}
            </p>
          )}
          {vinDecoder.vehicleInfo && !vinDecoder.isDecoding && !vinDecoder.error && (
            <p className='text-xs text-green-600 flex items-center gap-1'>
              <CheckCircle className='h-3 w-3' />
              {vinDecoder.vehicleInfo.year} {vinDecoder.vehicleInfo.make}{' '}
              {vinDecoder.vehicleInfo.model}
            </p>
          )}
        </div>

        <div className='space-y-2'>
          <Label htmlFor={reasonId}>
            Reason for Stop <span className='text-destructive'>*</span>
          </Label>
          <Select value={reason} onValueChange={setReason}>
            <SelectTrigger id={reasonId}>
              <SelectValue placeholder='Select a reason' />
            </SelectTrigger>
            <SelectContent>
              {reasonOptions.map(r => (
                <SelectItem key={r.value} value={r.value}>
                  {r.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <FieldError message={errors['manual_stop_reason']} />
        </div>

        {reason === 'other' && (
          <div className='space-y-2'>
            <Label htmlFor={`manual-stop-custom-reason-${index}`}>
              Custom Reason <span className='text-destructive'>*</span>
            </Label>
            <Input
              id={`manual-stop-custom-reason-${index}`}
              placeholder='Enter reason'
              value={customReason}
              onChange={e => setCustomReason(e.target.value)}
              maxLength={MANUAL_STOP_LIMITS.customReason}
            />
            <FieldError message={errors['custom_reason']} />
          </div>
        )}

        {showCampaignField && (
          <div className='space-y-2'>
            <Label htmlFor={`manual-stop-campaign-${index}`}>Campaign (Optional)</Label>
            <Input
              id={`manual-stop-campaign-${index}`}
              placeholder='Search campaigns...'
              value={campaignSearch}
              onChange={e => setCampaignSearch(e.target.value)}
            />
            {campaignSearch && filteredCampaigns.length > 0 && (
              <div className='border rounded-lg max-h-32 overflow-y-auto'>
                {filteredCampaigns.slice(0, 5).map((campaign: Campaign) => (
                  <button
                    type='button'
                    key={campaign.campaign_number}
                    onClick={() => handleSelectCampaign(campaign)}
                    className={cn(
                      'w-full text-left p-2 hover:bg-muted text-sm',
                      'border-b last:border-0 flex flex-col gap-0.5'
                    )}
                  >
                    <span className='font-mono truncate'>{campaign.campaign_number}</span>
                    {campaign.recall_title && (
                      <span className='text-muted-foreground text-xs truncate'>
                        {campaign.recall_title}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            )}
            {selectedCampaigns.length > 0 && (
              <div className='space-y-2'>
                {selectedCampaigns.map(c => (
                  <div
                    key={c.campaign_number}
                    className={cn(
                      'flex items-center justify-between gap-2 p-2 rounded bg-card',
                      'border border-blue-500/30 dark:border-blue-400/30'
                    )}
                  >
                    <div className='flex-1 min-w-0'>
                      <div className='flex items-baseline gap-2'>
                        <Badge
                          variant='outline'
                          className={cn(
                            'text-xs shrink-0 bg-blue-500/10',
                            'text-blue-700 dark:text-blue-300',
                            'border-blue-500/30 dark:border-blue-400/30'
                          )}
                        >
                          {c.campaign_number.split('/')[0] || c.campaign_number}
                        </Badge>
                        {c.recall_title && (
                          <span className='text-sm text-foreground font-medium truncate'>
                            {c.recall_title}
                          </span>
                        )}
                      </div>
                    </div>
                    <button
                      type='button'
                      onClick={() => handleRemoveCampaign(c.campaign_number)}
                      className='p-1 hover:bg-muted rounded transition-colors shrink-0'
                      aria-label={`Remove ${c.campaign_number}`}
                    >
                      <X className='h-3.5 w-3.5 text-muted-foreground' />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    );
  }
);
VehicleEntryRow.displayName = 'VehicleEntryRow';
